import { Share2, Gift, Flame, UserPlus, Mail, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useState } from 'react';

import { useSocialTaskStatus, useCompleteSocialTask, SocialTaskType } from '@/hooks/useSocialTasks';
import { useEmailVerificationStatus } from '@/hooks/useEmailVerification';
import { useWrappedData } from '@/hooks/useWrapped';
import { EmailVerificationDialog } from '@/components/EmailVerificationDialog';
import { AppRoutesEnum } from '@/lib/constants';

interface TaskItem {
  id: string;
  name: string;
  points: number;
  icon: React.ReactNode;
  description?: string;
  section: 'genesis' | 'limited-time' | 'permanent';
  socialTaskType?: SocialTaskType;
  link?: string;
  navigateTo?: string;
}

const TASKS: TaskItem[] = [
  // Genesis Wrapped 2025 - Exclusive Rewards
  {
    id: 'view-wrapped',
    name: 'View Your Wrapped 2025',
    points: 100,
    icon: <Sparkles className="w-5 h-5" />,
    description: 'View your personalized Junk.Fun Wrapped and earn 100 bonus points + "Genesis Wrapped 2025" badge (one-time)',
    section: 'genesis',
    navigateTo: AppRoutesEnum.Wrapped,
  },
  {
    id: 'share-wrapped',
    name: 'Share Your Wrapped 2025',
    points: 500,
    icon: <Sparkles className="w-5 h-5" />,
    description: 'Share your Wrapped 2025 results and earn 500 bonus points (one-time)',
    section: 'genesis',
    navigateTo: AppRoutesEnum.Wrapped,
  },

  // Limited-Time Tasks
  {
    id: 'share-chest',
    name: 'Share Chest Opening Result',
    points: 100,
    icon: <Share2 className="w-5 h-5" />,
    description: 'Share your chest opening results: 100 points (once per day, resets at UTC 0)',
    section: 'limited-time',
    navigateTo: AppRoutesEnum.Chest,
  },
  {
    id: 'share-lottery',
    name: 'Share Points Lottery Result',
    points: 100,
    icon: <Share2 className="w-5 h-5" />,
    description: 'Share your Points Lottery win: 100 points (once per day, resets at UTC 0)',
    section: 'limited-time',
    navigateTo: AppRoutesEnum.Rewards,
  },
  {
    id: 'share-junk',
    name: 'Share Junk Result',
    points: 20,
    icon: <Share2 className="w-5 h-5" />,
    description: 'Share your token or NFT junk results: 20 points (once per day, resets at UTC 0)',
    section: 'limited-time',
    navigateTo: AppRoutesEnum.Home,
  },

  // Permanent Tasks
  {
    id: 'verify-email',
    name: 'Verify Email',
    points: 100,
    icon: <Mail className="w-5 h-5" />,
    description: 'Verify your email address and earn 100 points (one-time reward)',
    section: 'permanent',
    socialTaskType: 'email',
  },
  {
    id: 'open-chest',
    name: 'Open Chest',
    points: 500,
    icon: <Gift className="w-5 h-5" />,
    description: 'Open 1 chest: Earn 500 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Chest,
  },
  {
    id: 'junk-token',
    name: 'Junk Token',
    points: 100,
    icon: <Flame className="w-5 h-5" />,
    description: 'Burn and recycle 1 token: 100 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Home,
  },
  {
    id: 'junk-nft',
    name: 'Junk NFT',
    points: 200,
    icon: <Flame className="w-5 h-5" />,
    description: 'Burn and recycle 1 NFT: 200 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Home,
  },
  {
    id: 'dust-swapping',
    name: 'Dust Swapping',
    points: 0,
    icon: <RefreshCw className="w-5 h-5" />,
    description: 'Recycle tokens with remaining value through Junk and Dust Swapping to earn points proportional to the recovery value',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Home,
  },
  {
    id: 'referral-inviter-dust-swapping',
    name: 'Referral-Inviter-Dust Swapping',
    points: 160,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As inviter, each time your invited user recycles a token via Dust Swapping: 160 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-invitee-dust-swapping',
    name: 'Referral-Invitee-Dust Swapping',
    points: 80,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As invitee, each time you recycle a token via Dust Swapping: 80 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-inviter-junk-token',
    name: 'Inviter: Referred User Junks Token',
    points: 80,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As inviter, each time your referred user burns and recycles a token: 80 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-inviter-junk-nft',
    name: 'Inviter: Referred User Junks NFT',
    points: 160,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As inviter, each time your referred user burns and recycles an NFT: 160 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-inviter-open-chest',
    name: 'Inviter: Referred User Opens Chest',
    points: 400,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As inviter, each time your referred user opens a chest: 400 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-invitee-junk-token',
    name: 'Invitee: Junk Token',
    points: 40,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As invitee, each time you burn and recycle a token: 40 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-invitee-junk-nft',
    name: 'Invitee: Junk NFT',
    points: 80,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As invitee, each time you burn and recycle an NFT: 80 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
  {
    id: 'referral-invitee-open-chest',
    name: 'Invitee: Open Chest',
    points: 200,
    icon: <UserPlus className="w-5 h-5" />,
    description: 'As invitee, each time you open a chest: 200 points',
    section: 'permanent',
    navigateTo: AppRoutesEnum.Referral,
  },
];

const SECTION_LABELS = {
  'genesis': '🎁 Genesis Wrapped 2025',
  'limited-time': 'Limited-Time',
  'permanent': 'Permanent',
};

export function EarnPointsSection() {
  const { data: socialTaskStatus } = useSocialTaskStatus();
  const { data: emailStatus, refetch: refetchEmailStatus } = useEmailVerificationStatus();
  const { data: wrappedData } = useWrappedData();
  const completeSocialTask = useCompleteSocialTask();
  const navigate = useNavigate();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Helper to check if a wrapped task is completed
  const isWrappedTaskCompleted = (taskId: string): boolean => {
    if (!wrappedData) return false;
    if (taskId === 'view-wrapped') return wrappedData.viewPointsAwarded;
    if (taskId === 'share-wrapped') return wrappedData.sharePointsAwarded;
    return false;
  };

  // Group tasks by section
  const tasksBySection = TASKS.reduce((acc, task) => {
    if (!acc[task.section]) {
      acc[task.section] = [];
    }
    acc[task.section].push(task);
    return acc;
  }, {} as Record<string, TaskItem[]>);

  const handleSocialTaskClick = async (task: TaskItem) => {
    if (!task.socialTaskType) return;

    // Wait for social task status to load before proceeding
    if (!socialTaskStatus) {
      toast.error('Loading task status, please try again');
      return;
    }

    // Check if already completed
    const isCompleted = socialTaskStatus[task.socialTaskType];
    if (isCompleted) {
      toast.info('You have already completed this task');
      return;
    }

    // Open the social link in a new tab
    if (task.link) {
      window.open(task.link, '_blank', 'noopener,noreferrer');
    }

    // Wait a bit for user to complete the task, then claim points
    setTimeout(async () => {
      try {
        await completeSocialTask.mutateAsync(task.socialTaskType!);
      } catch (error) {
        // Error is already handled by the mutation's onError callback
        console.error('Failed to complete social task:', error);
      }
    }, 2000);
  };

  const handleEmailVerificationSuccess = async () => {
    // Complete the email social task to award 100 points
    try {
      await completeSocialTask.mutateAsync('email');
      // Refetch email verification status to update the button
      await refetchEmailStatus();
    } catch (error) {
      // Error already handled by the mutation hook
      console.error('Failed to complete email social task:', error);
    }
  };

  const handleTaskClick = (task: TaskItem) => {
    if (task.socialTaskType === 'email') {
      // Special handling for email verification
      setEmailDialogOpen(true);
    } else if (task.socialTaskType) {
      handleSocialTaskClick(task);
    } else if (task.navigateTo) {
      navigate(task.navigateTo);
    }
  };

  return (
    <div className="rounded-2xl border overflow-hidden flex flex-col" style={{ 
      maxHeight: '600px',
      backgroundColor: 'rgba(26, 26, 26, 0.8)',
      borderColor: '#50E4F9',
      borderWidth: '2px',
    }}>
      {/* Section Header */}
      <div className="p-6 pb-4 flex-shrink-0">
        <h2
          className="text-2xl text-white mb-2"
          style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
        >
          Earn More Points
        </h2>
        <p className="text-sm text-white/60" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Complete tasks to earn points and increase your chances to win in the Points Lottery
        </p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
        {/* Genesis Wrapped 2025 Section */}
        {tasksBySection['genesis'] && (
          <div className="space-y-3">
            {/* Section Header with Lines */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(199, 125, 255, 0.4) 50%, rgba(199, 125, 255, 0.4) 100%)' }} />
              <h3
                className="text-base"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: '#C77DFF',
                }}
              >
                {SECTION_LABELS['genesis']}
              </h3>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(199, 125, 255, 0.4) 0%, rgba(199, 125, 255, 0.4) 50%, transparent 100%)' }} />
            </div>

            {/* Genesis Task Cards */}
            <div className="space-y-3">
              {tasksBySection['genesis'].map((task) => {
                // Use wrapped data for completion status instead of social tasks
                const isCompleted = isWrappedTaskCompleted(task.id);
                const isClickable = !isCompleted;

                return (
                  <div
                    key={task.id}
                    className={`p-4 rounded-2xl transition-all duration-200 ${
                      isCompleted ? 'bg-emerald-500/20' : ''
                    }`}
                    style={{
                      background: isCompleted ? undefined : 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 50%, #E0AAFF 100%)',
                      border: '2px solid #C77DFF',
                      boxShadow: isCompleted ? undefined : '0 4px 20px rgba(157, 78, 221, 0.3)',
                    }}
                  >
                    <div className="flex items-center justify-between gap-4">
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className="text-base font-bold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              fontWeight: 700,
                              color: '#FFFFFF',
                            }}
                          >
                            {task.name}
                          </h4>
                          <span
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              background: '#FFD700',
                              color: '#000000',
                            }}
                          >
                            EXCLUSIVE
                          </span>
                        </div>
                        {task.description && (
                          <p
                            className="text-sm"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              color: '#FFFFFF',
                              opacity: 0.9,
                            }}
                          >
                            {task.description}
                          </p>
                        )}
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => isClickable && handleTaskClick(task)}
                        disabled={isCompleted}
                        className="flex-shrink-0 px-8 py-2.5 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          fontSize: '14px',
                          background: isCompleted ? '#4CAF50' : '#FFFFFF',
                          color: isCompleted ? '#FFFFFF' : '#000000',
                          borderRadius: '100px',
                          whiteSpace: 'nowrap',
                          minWidth: '180px',
                          textAlign: 'center',
                        }}
                      >
                        {isCompleted ? '✓ COMPLETED' : 'GO TO WRAPPED'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Limited-Time Section */}
        {tasksBySection['limited-time'] && (
          <div className="space-y-3">
          {/* Section Header with Lines */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.2) 100%)' }} />
            <h3
              className="text-base text-[#50E4F9]"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {SECTION_LABELS['limited-time']}
            </h3>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)' }} />
          </div>

          {/* Horizontal Task Cards */}
          <div className="space-y-3">
            {tasksBySection['limited-time'].map((task) => {
              const isCompleted = task.socialTaskType && socialTaskStatus?.[task.socialTaskType];
              const isClickable = !isCompleted;

              return (
                <div
                  key={task.id}
                  className={`p-4 rounded-2xl transition-all duration-200 ${
                    isCompleted ? 'bg-emerald-500/20' : ''
                  }`}
                  style={{
                    background: isCompleted ? undefined : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4
                          className="text-base font-bold"
                          style={{ 
                            fontFamily: 'Outfit, sans-serif', 
                            fontWeight: 700,
                            color: '#000000',
                          }}
                        >
                          {task.name}
                        </h4>
                        {task.socialTaskType && (
                          <span
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              background: '#EF4444',
                              color: '#FFFFFF',
                            }}
                          >
                            Limited Time
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p
                          className="text-sm"
                          style={{ 
                            fontFamily: 'Outfit, sans-serif',
                            color: '#000000',
                            opacity: 0.8,
                          }}
                        >
                          {task.description}
                        </p>
                      )}
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => isClickable && handleTaskClick(task)}
                      disabled={isCompleted}
                      className="flex-shrink-0 px-8 py-2.5 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        fontSize: '14px',
                        background: '#FFFFFF',
                        color: '#000000',
                        borderRadius: '100px',
                        whiteSpace: 'nowrap',
                        minWidth: '180px',
                        textAlign: 'center',
                      }}
                    >
                      {isCompleted ? '✓ COMPLETED' : task.id === 'follow-twitter' ? 'GO TO TWITTER' : task.id === 'join-telegram' ? 'GO TO TELEGRAM' : task.id === 'share-chest' ? 'GO TO CHEST' : task.id === 'share-lottery' ? 'GO TO REWARDS' : 'GO TO JUNK'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Permanent Section */}
      {tasksBySection['permanent'] && (
        <div className="space-y-3">
          {/* Section Header with Lines */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.2) 100%)' }} />
            <h3
              className="text-base text-[#50E4F9]"
              style={{ 
                fontFamily: 'Outfit, sans-serif', 
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {SECTION_LABELS['permanent']}
            </h3>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)' }} />
          </div>

          {/* Horizontal Task Cards */}
          <div className="space-y-3">
            {tasksBySection['permanent'].map((task) => {
              // Use email verification status from database for email task, social task status for others
              const isCompleted = task.socialTaskType === 'email'
                ? emailStatus?.verified ?? false
                : task.socialTaskType && socialTaskStatus?.[task.socialTaskType];
              const buttonText = task.id === 'verify-email' ? (isCompleted ? '✓ COMPLETED' : 'VERIFY EMAIL') :
                                task.id === 'open-chest' ? 'GO TO CHEST' :
                                (task.id === 'junk-token' || task.id === 'junk-nft' || task.id === 'dust-swapping') ? 'GO TO JUNK' :
                                'GO TO REFERRAL';

              return (
                <div
                  key={task.id}
                  className="p-4 rounded-2xl bg-white transition-all duration-200"
                >
                  <div className="flex items-center justify-between gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4
                        className="text-base font-bold mb-1"
                        style={{ 
                          fontFamily: 'Outfit, sans-serif', 
                          fontWeight: 700,
                          color: '#000000',
                        }}
                      >
                        {task.name}
                      </h4>
                      {task.description && (
                        <p
                          className="text-sm"
                          style={{ 
                            fontFamily: 'Outfit, sans-serif',
                            color: '#000000',
                            opacity: 0.7,
                          }}
                        >
                          {task.description}
                        </p>
                      )}
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => handleTaskClick(task)}
                      disabled={isCompleted}
                      className="flex-shrink-0 px-8 py-2.5 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        fontSize: '14px',
                        background: isCompleted ? '#4CAF50' : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                        color: isCompleted ? '#FFFFFF' : '#000000',
                        borderRadius: '100px',
                        whiteSpace: 'nowrap',
                        minWidth: '180px',
                        textAlign: 'center',
                      }}
                    >
                      {buttonText}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>

      <EmailVerificationDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        onVerificationSuccess={handleEmailVerificationSuccess}
      />
    </div>
  );
}
